<template>
    <div v-if="!showOnlyFrame" :data-state="playerState" class="youtube-player">
        <div :class="{ fixed: fixed, fullscreened: fullscreened, mobile: mobile }" class="youtube-player-container">
            <PlayerControlsWrapper
                ref="controls"
                @toggle:mute="toggleMute"
                @toggle:fs="toggleFullScreen"
                @toggle:pip="togglePip"
                @change:volume="setVolume($event * 100)"
                @change:quality="setQuality($event)"
                @change:rate="setRate($event)"
                @update:seek="updateSeek($event)"
                @forwards="forwards"
                @backwards="backwards"
                @play="playVideo"
                @pause="pauseVideo"
                :quality="getQuality()"
                :componentId="componentId"
                :rate="getRate()"
                :quality-levels="getQualityLevels()"
                :rate-levels="geRateLevels()"
                :player-state="paused ? PLAYER_STATES.UNINIT : playerState"
                :preview="
                    playerState === PLAYER_STATES.PAUSED && !isShowPreviewOnPause
                        ? ''
                        : preview || `http://img.youtube.com/vi/${extractYoutubeKey(this.link)}/maxresdefault.jpg`
                "
                :muted="muted"
                :time="time"
                :duration="duration"
                :volume="volume / 100"
                :fullscreened="fullscreened"
            />
            <div class="youtube-player-video-container" :class="{ vertical: isVertical }">
                <div
                    class="frame-container"
                    :style="{ pointerEvents: playerState === PLAYER_STATES.UNSTARTED && false ? 'all' : 'none' }"
                >
                    <div class="player" :id="componentId"></div>
                </div>
            </div>
        </div>
    </div>
    <iframe
        style="aspect-ratio: 16 / 9"
        v-else
        width="100%"
        height="auto"
        :src="`https://www.youtube.com/embed/${extractYoutubeKey(link)}?autoplay=1`"
        title="YouTube video player"
        frameborder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        referrerpolicy="strict-origin-when-cross-origin"
        allowfullscreen
    ></iframe>
</template>

<script>
import PlayerControlsWrapper, { PLAYER_STATES } from "@components/Players/components/PlayerControlsWrapper.vue"
import BugIcon from "@icons/BugIcon.vue"
import { EventBus, EventsList } from "~events"

// --- Start of robust script loader logic ---
// This ensures the YouTube IFrame API is loaded only once, and any number of
// player components on the same page can be initialized correctly without race conditions.
if (typeof window.onYouTubeIframeAPIReadyCallbacks === "undefined") {
    window.onYouTubeIframeAPIReadyCallbacks = []
    window.youTubeApiLoaded = false

    window.onYouTubeIframeAPIReady = function () {
        window.youTubeApiLoaded = true
        window.onYouTubeIframeAPIReadyCallbacks.forEach(callback => {
            if (typeof callback === "function") {
                callback()
            }
        })
        window.onYouTubeIframeAPIReadyCallbacks = [] // Clear after execution
    }
}
// --- End of robust script loader logic ---

/**
 * @name YouTubeVideoPlayerContent
 * @description A custom YouTube video player component that uses the YouTube IFrame Player API
 * to provide a custom UI and enhanced controls. It handles player state, custom controls,
 * fullscreen mode, and robust API loading.
 */
export default {
    components: { PlayerControlsWrapper },
    props: {
        isShowPreviewOnPause: {
            type: Boolean,
            default: false,
            required: false
        },
        infoUnitId: {
            type: Number,
            default: 0
        },
        isVertical: {
            type: Boolean,
            default: false,
            required: false
        },
        link: {
            type: String,
            default: ""
        },
        // Optional host for the YouTube player (e.g., 'https://www.youtube-nocookie.com').
        host: {
            type: String,
            default: ""
        },
        preview: {
            type: String,
            default: ""
        },
        // Timeout in milliseconds for the YouTube IFrame API to load.
        apiTimeout: {
            type: Number,
            default: 10000
        }
    },
    name: "YouTubeVideoPlayerContent",
    data() {
        return {
            player: null, // The YouTube player instance.
            volume: 100,
            // Fallback "fixed" fullscreen mode for browsers that don't support the Fullscreen API.
            fixed: false,
            muted: false,
            fullscreened: false,
            mobile: false,
            playerState: PLAYER_STATES.UNINIT,
            PLAYER_STATES: PLAYER_STATES, // Enum of possible player states.
            duration: 0,
            time: 0,
            rate: 1,
            quality: "unknown",
            // A flag used for showing a loader animation on state change to improve perceived performance.
            paused: false,
            componentId: null, // A unique ID for the player's DOM element.
            showOnlyFrame: false,
            timeUpdateInterval: null,
            apiLoadTimeout: null,
            // Timer to detect if player is stuck in UNSTARTED/BUFFERING states
            stuckStateTimer: null,
            // Flag to prevent async operations on a destroyed component.
            isDestroyed: false,
            // Flag to track if video has successfully started playing at least once
            hasStartedPlayingOnce: false
        }
    },

    computed: {
        /**
         * Check if debug mode is enabled (dev environment or ?debug=1 in URL)
         */
        isDebugMode() {
            const isDev = process.env.NODE_ENV === "development"
            const hasDebugParam = new URLSearchParams(window.location.search).get("debug") === "1"
            return isDev || hasDebugParam
        }
    },

    /**
     * Called when the component is mounted.
     * Sets up the component, detects mobile/fullscreen capabilities, and initiates player loading.
     */
    mounted() {
        this.componentId = "ytb-id-" + this._uid
        this.mobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) || window.innerWidth < 600

        this.debugLog("mounted", "Component mounted", {
            link: this.link,
            mobile: this.mobile,
            isIOS: this.isIOS(),
            userAgent: navigator.userAgent
        })

        this.fullScreenAvailable =
            document.fullscreenEnabled ||
            document.mozFullscreenEnabled ||
            document.webkitFullscreenEnabled ||
            document.msFullscreenEnabled

        if (this.fullScreenAvailable && !this.isIOS()) {
            const exitHandler = () => {
                if (!document.webkitIsFullScreen && !document.mozFullScreen && !document.msFullscreenElement) {
                    setTimeout(() => {
                        if (this.fullscreened) {
                            this.fullscreened = false
                        }
                    }, 100)
                }
            }
            document.addEventListener("fullscreenchange", exitHandler, false)
            document.addEventListener("mozfullscreenchange", exitHandler, false)
            document.addEventListener("MSFullscreenChange", exitHandler, false)
            document.addEventListener("webkitfullscreenchange", exitHandler, false)
        }

        this.initScript()
    },

    /**
     * Called just before the component is destroyed.
     * Cleans up intervals and destroys the YouTube player instance to prevent memory leaks.
     */
    beforeDestroy() {
        this.isDestroyed = true
        if (this.apiLoadTimeout) {
            clearTimeout(this.apiLoadTimeout)
        }
        if (this.stuckStateTimer) {
            clearTimeout(this.stuckStateTimer)
        }
        // Clear interval on unmount
        if (this.timeUpdateInterval) {
            clearInterval(this.timeUpdateInterval)
        }

        // Destroy the player
        if (this.player && typeof this.player.destroy === "function") {
            try {
                this.player.destroy()
            } catch (error) {
                console.warn("Error destroying YouTube player:", error)
            }
        }
    },
    methods: {
        /**
         * Centralized debug logging method
         * @param {string} method - Method name where log is called
         * @param {string} message - Log message
         * @param {object} data - Additional data to log
         */
        debugLog(method, message, data = null) {
            if (!this.isDebugMode) return

            const prefix = `[YT Player ${this.componentId}]`
            const timestamp = new Date().toISOString()
            const logData = {
                timestamp,
                componentId: this.componentId,
                videoId: this.extractYoutubeKey(this.link),
                method,
                message,
                playerState: this.getStateName(this.playerState),
                showOnlyFrame: this.showOnlyFrame,
                ...(data || {})
            }

            console.log(`${prefix} [${method}] ${message}`, logData)
        },

        /**
         * Get human-readable player state name
         */
        getStateName(state) {
            const stateNames = {
                [PLAYER_STATES.UNINIT]: "UNINIT",
                [PLAYER_STATES.UNSTARTED]: "UNSTARTED",
                [PLAYER_STATES.ENDED]: "ENDED",
                [PLAYER_STATES.PLAYING]: "PLAYING",
                [PLAYER_STATES.PAUSED]: "PAUSED",
                [PLAYER_STATES.BUFFERING]: "BUFFERING",
                [PLAYER_STATES.CUED]: "CUED"
            }
            return stateNames[state] || `UNKNOWN(${state})`
        },

        getQualityLevels() {
            if (this.player && this.player.getAvailableQualityLevels) {
                return this.player.getAvailableQualityLevels()
            }
            return []
        },

        geRateLevels() {
            if (this.player && this.player.getAvailablePlaybackRates) {
                return this.player.getAvailablePlaybackRates()
            }
            return []
        },

        getQuality() {
            return this.quality || "unknown"
        },

        getRate() {
            return this.rate || 1
        },

        pauseVideo() {
            this.player.pauseVideo()
        },

        backwards() {
            this.updateSeek(this.player.getCurrentTime() - 10)
        },

        forwards() {
            this.updateSeek(this.player.getCurrentTime() + 10)
        },

        playVideo() {
            this.debugLog("playVideo", "playVideo() called", {
                playerExists: !!this.player,
                canPlay: this.player && typeof this.player.playVideo === "function",
                currentState: this.getStateName(this.playerState)
            })

            if (!this.player || typeof this.player.playVideo !== "function") {
                this.debugLog("playVideo", "PLAYER NOT READY - Cannot play video", {
                    playerExists: !!this.player,
                    playVideoExists: this.player && typeof this.player.playVideo
                })
                console.error("YouTube player is not ready yet")
                return
            }

            // Play only if the player is not already playing or buffering to prevent weird states.
            if ([PLAYER_STATES.PLAYING, PLAYER_STATES.BUFFERING].indexOf(this.playerState) === -1) {
                this.debugLog("playVideo", "Calling player.playVideo()", {
                    fromState: this.getStateName(this.playerState)
                })
                this.player.playVideo()
                window["activePlayer"] = this.componentId
            } else {
                this.debugLog("playVideo", "BLOCKED - playVideo() in blocked state, ignoring", {
                    state: this.getStateName(this.playerState)
                })
                console.warn(`playVideo() called in a blocked state: ${this.playerState}. Ignoring call.`)
            }
        },

        /**
         * YouTube API callback for when the player is ready.
         */
        onPlayerReady(event) {
            this.debugLog("onPlayerReady", "Player ready event triggered", {
                playerExists: !!this.player,
                canGetDuration: this.player && typeof this.player.getDuration === "function"
            })

            if (this.player && typeof this.player.getDuration === "function") {
                this.duration = this.player.getDuration()
                this.debugLog("onPlayerReady", "Got video duration", { duration: this.duration })
            }

            // Restore volume from local storage.
            if (localStorage.getItem("yt-volume")) {
                const value = localStorage.getItem("yt-volume")
                this.volume = value
                this.player.setVolume(value)
                this.debugLog("onPlayerReady", "Restored volume from localStorage", { volume: value })
            }

            this.debugLog("onPlayerReady", "Setting player state to UNSTARTED and calling playVideo()")
            this.setPlayerState(PLAYER_STATES.UNSTARTED)
            this.playVideo()
            this.$emit("ready", this.player)
        },

        /**
         * Updates the internal player state and emits events.
         * @param {number} state - The new player state from the YouTube API.
         */
        setPlayerState(state) {
            const oldState = this.playerState
            this.debugLog("setPlayerState", "State change requested", {
                from: this.getStateName(oldState),
                to: this.getStateName(state),
                fromCode: oldState,
                toCode: state
            })

            const setLoader = reason => {
                this.debugLog("setPlayerState", "Setting loader (paused=true)", { reason })
                this.paused = true
            }

            const hideLoader = reason => {
                if (this.paused) {
                    this.debugLog("setPlayerState", "Hiding loader (paused=false)", { reason })
                    this.paused = false
                }
            }

            // Handle PLAYING state - hide loader and clear stuck timer
            if (state === PLAYER_STATES.PLAYING) {
                this.time = this.player.getCurrentTime()

                const isFirstPlay = !this.hasStartedPlayingOnce
                if (isFirstPlay) {
                    this.hasStartedPlayingOnce = true
                    this.debugLog("setPlayerState", "State is PLAYING - FIRST successful start!", {
                        currentTime: this.time,
                        firstPlay: true
                    })
                } else {
                    this.debugLog("setPlayerState", "State is PLAYING - resumed", {
                        currentTime: this.time,
                        firstPlay: false
                    })
                }

                hideLoader("video started playing")

                // Clear stuck state timer - we successfully started playing
                if (this.stuckStateTimer) {
                    clearTimeout(this.stuckStateTimer)
                    this.stuckStateTimer = null
                    this.debugLog("setPlayerState", "Cleared stuck state timer - playing successfully")
                }

                // Attempt to disable captions/subtitles.
                try {
                    this.player.unloadModule("captions")
                    this.player.unloadModule("cc")
                } catch (e) {
                    // This can fail if the module isn't loaded, which is fine.
                }
            }

            // Show loader when resuming from PAUSED
            if (oldState === PLAYER_STATES.PAUSED && state !== PLAYER_STATES.PAUSED) {
                this.debugLog("setPlayerState", "Resuming from PAUSED state")
                setLoader("resuming from pause")
            }

            // Handle PAUSED state
            if (state === PLAYER_STATES.PAUSED) {
                this.debugLog("setPlayerState", "New state is PAUSED - emitting pause event")
                hideLoader("video paused")

                // Clear stuck timer when paused
                if (this.stuckStateTimer) {
                    clearTimeout(this.stuckStateTimer)
                    this.stuckStateTimer = null
                }

                this.$emit("pause")
            }

            // Show loader on first UNSTARTED (initial state before first play attempt)
            if (state === PLAYER_STATES.UNSTARTED && oldState === PLAYER_STATES.UNINIT) {
                this.debugLog("setPlayerState", "Initial UNSTARTED state")
                setLoader("initial play attempt")
            }

            // Handle ENDED state
            if (state === PLAYER_STATES.ENDED) {
                this.debugLog("setPlayerState", "New state is ENDED - emitting ended event")
                hideLoader("video ended")

                // Clear stuck timer when ended
                if (this.stuckStateTimer) {
                    clearTimeout(this.stuckStateTimer)
                    this.stuckStateTimer = null
                }

                this.$emit("ended")
            }

            // Emit play event when transitioning from UNSTARTED to BUFFERING
            if (oldState === PLAYER_STATES.UNSTARTED && state === PLAYER_STATES.BUFFERING) {
                this.debugLog("setPlayerState", "Transitioning from UNSTARTED to BUFFERING - emitting play")
                this.$emit("play")
            }

            // Detect stuck states - if player is stuck in UNSTARTED/BUFFERING for too long
            // Only start timer for initial playback attempt, not for buffering during playback
            if (
                (state === PLAYER_STATES.UNSTARTED || state === PLAYER_STATES.BUFFERING) &&
                !this.stuckStateTimer &&
                !this.hasStartedPlayingOnce
            ) {
                this.debugLog("setPlayerState", "Starting stuck state detection timer (5 seconds) for initial playback")
                let stuckDelay = 10000
                this.stuckStateTimer = setTimeout(() => {
                    const currentState = this.playerState
                    if (currentState === PLAYER_STATES.UNSTARTED || currentState === PLAYER_STATES.BUFFERING) {
                        this.debugLog(
                            "setPlayerState",
                            "⚠️ STUCK STATE DETECTED - Player stuck in " + this.getStateName(currentState),
                            {
                                stuckDuration: stuckDelay + "ms",
                                recommendation: "Possible AdBlock, network issue, or embed restriction",
                                currentState: this.getStateName(currentState),
                                paused: this.paused,
                                willSwitchToFallback: true
                            }
                        )

                        // Hide loader since we're stuck
                        hideLoader("stuck state detected")

                        // Automatically switch to iframe fallback
                        this.$emit("showDefault")
                    }
                    this.stuckStateTimer = null
                }, stuckDelay)
            }

            this.$set(this, "playerState", state)
            this.debugLog("setPlayerState", "State change completed", {
                newState: this.getStateName(state),
                paused: this.paused,
                stuckTimerActive: !!this.stuckStateTimer
            })
            this.update()
        },

        updateSeek(val) {
            this.time = val
            this.player.seekTo(val)
        },

        setQuality(val) {
            if (this.player) {
                this.quality = val
                this.player.setPlaybackQuality(val)
            }
        },

        onPlaybackRateChange(val) {
            this.rate = val.data
        },

        onPlaybackQualityChange(val) {
            this.quality = val.data
        },

        setRate(val) {
            if (this.player) {
                this.player.setPlaybackRate(val)
            }
        },

        setVolume(value) {
            if (this.player.isMuted()) {
                this.player.unMute()
                this.muted = false
            }

            this.player.setVolume(value)
            localStorage.setItem("yt-volume", value)
            this.volume = value
        },

        toggleMute() {
            if (this.player.isMuted()) {
                this.player.unMute()
                this.player.setVolume(50)
                this.volume = 50
                this.muted = false
            } else {
                this.player.mute()
                this.player.setVolume(0)
                this.volume = 0
                this.muted = true
            }
        },

        isInFullScreen() {
            return (
                (document.fullscreenElement && true) ||
                (document.webkitFullscreenElement && true) ||
                (document.mozFullScreenElement && true) ||
                (document.msFullscreenElement && true)
            )
        },

        togglePip() {
            const video = this.$el.querySelector(".youtube-player-container iframe")

            if (document.pictureInPictureElement) {
                document.exitPictureInPicture()
            } else if (document.pictureInPictureEnabled) {
                video.requestPictureInPicture()
            }
        },

        /**
         * Toggles fullscreen mode for the player.
         * Handles both native browser fullscreen and a fallback "fixed" mode.
         */
        toggleFullScreen() {
            const video = this.$el.querySelector(".youtube-player-container")

            this.fullscreened = !this.fullscreened

            if (this.fullscreened) {
                this.scrollTop = document.body.scrollTop
            } else {
                setTimeout(() => {
                    document.body.scrollTop = this.scrollTop
                }, 200)
            }

            // Fallback for browsers without native fullscreen support.
            if (!this.fullScreenAvailable) {
                this.fixed = !this.fixed
                return
            }

            // Native fullscreen handling.
            if (!this.isInFullScreen()) {
                if (video.requestFullScreen) {
                    video.requestFullScreen()
                } else if (video.mozRequestFullScreen) {
                    video.mozRequestFullScreen()
                } else if (video.webkitRequestFullScreen) {
                    video.webkitRequestFullScreen()
                } else if (video.webkitRequestFullscreen) {
                    video.webkitRequestFullscreen()
                } else if (video.webkitEnterFullscreen) {
                    video.webkitEnterFullScreen()
                }
            } else {
                if (document.exitFullscreen) {
                    document.exitFullscreen()
                } else if (document.webkitExitFullscreen) {
                    document.webkitExitFullscreen()
                } else if (document.mozCancelFullScreen) {
                    document.mozCancelFullScreen()
                } else if (document.msExitFullscreen) {
                    document.msExitFullscreen()
                }
            }
        },

        /**
         * Manages the loading of the YouTube IFrame API script.
         * This method ensures the API script is loaded only once, even if multiple
         * player components are on the same page. It uses a global callback queue
         * (`window.onYouTubeIframeAPIReadyCallbacks`) to collect initialization functions
         * from each player instance.
         *
         * When the YouTube API is ready, it calls the global `onYouTubeIframeAPIReady` function,
         * which then executes all queued callbacks.
         *
         * A critical part of this process is wrapping the `initPlayer` call in `this.$nextTick()`.
         * This is necessary to solve a race condition where the API might be ready before
         * Vue has finished rendering the player's target `<div>` in the DOM. `$nextTick`
         * defers the `initPlayer` execution until after the DOM has been updated, guaranteeing
         * that the target element exists.
         *
         * @see {@link https://developers.google.com/youtube/iframe_api_reference} for official API documentation.
         */
        initScript() {
            this.debugLog("initScript", "Starting script initialization", {
                youTubeApiLoaded: window.youTubeApiLoaded,
                apiTimeout: this.apiTimeout
            })

            const init = () => {
                this.debugLog("initScript", "API ready callback triggered")
                if (this.apiLoadTimeout) {
                    clearTimeout(this.apiLoadTimeout)
                }
                // Always wait for the next DOM tick to ensure the element exists
                this.$nextTick(() => {
                    this.debugLog("initScript", "Next tick - calling initPlayer")
                    this.initPlayer()
                })
            }

            if (window.youTubeApiLoaded) {
                this.debugLog("initScript", "YouTube API already loaded")
                init()
            } else {
                this.debugLog("initScript", "YouTube API not loaded yet, setting up callbacks and timeout")

                this.apiLoadTimeout = setTimeout(() => {
                    if (!window.youTubeApiLoaded && !this.isDestroyed) {
                        this.debugLog("initScript", "API LOAD TIMEOUT - API failed to load in time", {
                            apiTimeout: this.apiTimeout,
                            isDestroyed: this.isDestroyed
                        })
                        console.error("YouTube IFrame API failed to load in time.")
                        this.$emit("showDefault")
                    }
                }, this.apiTimeout)

                window.onYouTubeIframeAPIReadyCallbacks.push(init)

                if (!document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) {
                    this.debugLog("initScript", "Injecting YouTube IFrame API script")
                    const tag = document.createElement("script")
                    tag.src = "https://www.youtube.com/iframe_api"

                    tag.onerror = () => {
                        this.debugLog("initScript", "SCRIPT LOAD ERROR - Failed to load YouTube IFrame API script", {
                            possibleCause: "AdBlock or network issue"
                        })
                        console.error(
                            "Failed to load YouTube IFrame API script. Possibly blocked by an extension (e.g., AdBlock) or a network issue."
                        )
                        if (this.apiLoadTimeout) {
                            clearTimeout(this.apiLoadTimeout)
                        }
                        if (!this.isDestroyed) {
                            this.$emit("showDefault")
                        }
                    }

                    const firstScriptTag = document.getElementsByTagName("script")[0]
                    firstScriptTag.parentNode.insertBefore(tag, firstScriptTag)
                } else {
                    this.debugLog("initScript", "YouTube IFrame API script already exists in DOM")
                }
            }
        },

        seek() {
            return 0
        },

        isIOS() {
            return (
                ["iPad Simulator", "iPhone Simulator", "iPod Simulator", "iPad", "iPhone", "iPod"].includes(
                    navigator.platform
                ) ||
                // iPad on iOS 13 detection
                (navigator.userAgent.includes("Mac") && "ontouchend" in document)
            )
        },

        /**
         * Initializes the YT.Player instance once the API is ready and the component is mounted.
         */
        initPlayer() {
            this.debugLog("initPlayer", "Starting player initialization", {
                isDestroyed: this.isDestroyed,
                YTAvailable: typeof window.YT !== "undefined",
                YTPlayerAvailable: typeof window.YT !== "undefined" && typeof window.YT.Player !== "undefined"
            })

            if (this.isDestroyed) {
                this.debugLog("initPlayer", "Component is destroyed, aborting initialization")
                return
            }

            if (typeof window.YT === "undefined" || typeof window.YT.Player === "undefined") {
                this.debugLog("initPlayer", "YT.Player NOT AVAILABLE - called prematurely", {
                    YT: typeof window.YT,
                    YTPlayer: typeof window.YT !== "undefined" ? typeof window.YT.Player : "undefined"
                })
                console.error("YT.Player is not available! initPlayer was called prematurely.")
                this.$emit("showDefault")
                return
            }

            const videoId = this.extractYoutubeKey(this.link)

            this.debugLog("initPlayer", "Extracted video ID", { videoId })

            if (!document.getElementById(this.componentId)) {
                this.debugLog("initPlayer", "DOM ELEMENT NOT FOUND", {
                    componentId: this.componentId,
                    domExists: !!document.getElementById(this.componentId)
                })
                console.error(`Element with id "${this.componentId}" not found in DOM for player.`)
                this.$emit("showDefault")
                return
            }

            /**
             * Player variables to configure the YouTube player.
             * These parameters control the player's appearance and behavior.
             * @see {@link https://developers.google.com/youtube/player_parameters#Parameters} for a complete list of parameters.
             */
            const playerVars = {
                rel: 0, // Do not show related videos at the end.
                showinfo: 0, // Deprecated, but good to have for older players. Hides video title and uploader.
                modestbranding: 1, // Reduce the YouTube logo in the control bar.
                iv_load_policy: 3, // Do not show video annotations.
                muted: 0, // Player is not muted by default.
                enablejsapi: 1, // Enable the JavaScript API. Required for this component.
                origin: window.location.origin, // Security measure for the JS API.
                playsinline: 1, // Allow inline playback on iOS.
                autoplay: 0, // Do not autoplay the video on load.
                disablekb: 0, // Keep default keyboard controls enabled.
                controls: 0, // Hide the default YouTube controls, as we use custom ones.
                fs: 1, // Technically enables the fullscreen button, but our `controls: 0` hides it.
                start: this.extractStart(this.link) // Start video at a specific time if provided in URL.
            }

            this.debugLog("initPlayer", "Creating YT.Player instance", {
                videoId,
                playerVars,
                host: this.host || "https://www.youtube-nocookie.com"
            })

            this.player = new window.YT.Player(this.componentId, {
                height: window.innerHeight,
                width: window.innerWidth,
                videoId: videoId,
                playerVars: playerVars,
                host: this.host ? this.host : "https://www.youtube-nocookie.com",
                events: {
                    onPlaybackQualityChange: this.onPlaybackQualityChange.bind(this),
                    onPlaybackRateChange: this.onPlaybackRateChange.bind(this),
                    onReady: this.onPlayerReady.bind(this),
                    onError: this.onPlayerError.bind(this),
                    onStateChange: this.onPlayerStateChange.bind(this)
                }
            })

            this.debugLog("initPlayer", "YT.Player instance created successfully")

            EventBus.$on(EventsList.VIDEO_SET_SEEK, ({ unitId, seekTo }) => {
                if (this.infoUnitId === unitId) {
                    this.updateSeek(seekTo)
                }
            })
        },

        /**
         * YouTube API callback for player errors.
         */
        onPlayerError(event) {
            const errorMessages = {
                2: "Invalid parameter value",
                5: "HTML5 player error",
                100: "Video not found",
                101: "Video not allowed in embedded players",
                150: "Video not allowed in embedded players"
            }

            this.debugLog("onPlayerError", "PLAYER ERROR OCCURRED", {
                errorCode: event.data,
                errorMessage: errorMessages[event.data] || "Unknown error",
                event: event,
                willShowDefault: true
            })

            console.error(`YouTube Player Error for ${this.componentId}. Code: ${event.data}`, event)

            this.$emit("showDefault")

            const noti = text => {
                if (this.$notify) {
                    this.$notify({
                        position: "top-right",
                        color: "danger",
                        time: 5000,
                        title: this.t("expert.something_went_wrong"),
                        text: text,
                        icon: BugIcon
                    })
                }
            }
            switch (event.data) {
                case 2: {
                    noti("The request contains an invalid parameter value")
                    break
                }
                case 5: {
                    noti(
                        "The requested content cannot be played in an HTML5 player or another error related to the player has occurred."
                    )
                    fetch("https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js", {
                        method: "HEAD",
                        mode: "no-cors",
                        cache: "no-store"
                    })
                        .then(() => {})
                        .catch(() => {
                            noti("Please disable Adblock")
                        })
                    break
                }
                case 100: {
                    noti("The video requested was not found.")
                    break
                }
                case 101:
                case 150: {
                    noti("The owner of the requested video does not allow it to be played in embedded players")
                    break
                }
                default: {
                    console.error(event)
                }
            }
        },

        /**
         * YouTube API callback for player state changes. Manages the time update interval.
         * @param {object} event - The state change event object.
         */
        onPlayerStateChange(event) {
            this.debugLog("onPlayerStateChange", "YouTube API state change event", {
                newState: this.getStateName(event.data),
                stateCode: event.data,
                currentPlayerState: this.getStateName(this.playerState)
            })

            // Manage interval based on player state
            if (event.data === PLAYER_STATES.PLAYING) {
                // Start interval only when playing begins
                if (!this.timeUpdateInterval) {
                    this.debugLog("onPlayerStateChange", "Starting time update interval")
                    this.timeUpdateInterval = setInterval(() => {
                        if (this.player && typeof this.player.getCurrentTime === "function") {
                            this.time = this.player.getCurrentTime()

                            if (typeof this.player.getDuration === "function") {
                                this.$emit("timeupdate", {
                                    seconds: this.player.getCurrentTime(),
                                    duration: this.player.getDuration()
                                })
                            }
                        }
                    }, 1000)
                }
            } else {
                // Clear interval on pause, end, etc.
                if (this.timeUpdateInterval) {
                    this.debugLog("onPlayerStateChange", "Clearing time update interval", {
                        reason: `State changed to ${this.getStateName(event.data)}`
                    })
                    clearInterval(this.timeUpdateInterval)
                    this.timeUpdateInterval = null
                }
            }

            this.setPlayerState(event.data)
        },

        update() {
            this.didUpdate = true
            this.$nextTick(() => {
                this.didUpdate = false
            })
        },

        extractYoutubeKey(url) {
            const regExp =
                /^.*(?:(?:youtu\.be\/|v\/|vi\/|live\/|u\/\w\/|embed|shorts|\/)|(?:(?:watch)?\?v(?:i)?=|&v(?:i)?=))([^#&?]*).*/
            let match = url.match(regExp)
            let videoId = null
            if (match) {
                if (match[7] && match[7].length === 11) {
                    videoId = match[7]
                } else if (match[1] && match[1].length === 11) {
                    videoId = match[1]
                }
            }
            return videoId || false
        },

        extractStart(url_string) {
            try {
                let url = new URL(url_string)
                return parseInt(url.searchParams.get("t")) || 0
            } catch (e) {
                return 0
            }
        }
    },
    watch: {
        /**
         * Toggles a class on the body when the 'fixed' fullscreen mode changes.
         * This is a side-effect for styling purposes.
         */
        fixed(val) {
            if (val) {
                document.body.classList.add("body-black")
            } else {
                document.body.classList.remove("body-black")
            }
        }
    }
}
</script>



<style scoped lang="sass">
.youtube-player
    border-radius: 10px
    position: relative
    padding-bottom: 50%
    height: 0 !important
    .frame-container
        height: 100%
        width: 100%
        position: relative
        .black-loader
            position: absolute
            top: 0px
            bottom: 0
            left: 0
            right: 0
            width: 100%
            height: 100%
            z-index: 555
            background: #000000
            border-radius: 10px
    video
        width: 100%
        border-radius: 10px
    .youtube-player-video-container
        position: relative
        top: 0
        right: 0
        bottom: 0
        left: 0
        height: 100%
    .youtube-player-container
        position: absolute
        max-height: 100%
        height: 100%
        width: 100%
        border-radius: 10px
        overflow: hidden
        &.fullscreened
            border-radius: 0
            &::v-deep
                .controls-loader, .controls-play
                    border-radius: 0
        &.fixed
            position: fixed
            top: 0
            right: 0
            left: 0
            bottom: 0
            z-index: 9999999999
            background-color: #000
            display: flex
            align-items: center
            justify-content: center
            @media screen and (orientation: portrait)
                .frame-container
                    height: 100% !important
                    position: absolute
            @media screen and (orientation: landscape)
                .frame-container
                    height: auto
            .youtube-player-video-container
                height: max-content
                display: flex
                width: 100%
                &.vertical
                    height: 100% !important
                &::before
                    float: left
                    padding-top: 56.25%
                    content: ""
                &::after
                    display: block
                    content: ""
                    clear: both
            .actions
                border-radius: 0 !important

        &:fullscreen
            width: 100vw
            position: fixed
            .youtube-player-video-container
                @supports not (aspect-ratio: 16 / 9)
                    width: 100% !important
                    padding-top: 56.25%
                    top: 50% !important
                    position: relative
                    left: auto
                    right: auto
                    bottom: auto
                    height: auto
                    transform: translateY(-50%) !important
                    .frame-container
                        position: absolute !important
                        top: 0 !important
                        left: 0 !important
                        bottom: 0 !important
                        right: 0 !important
                @supports  (aspect-ratio: 16 / 9)
                    transform: translateY(-50%) !important
                    top: 50% !important
                    aspect-ratio: 16 / 9 !important
                    width: 100% !important
                    height: auto !important
                    max-height: 100% !important
                    &.vertical
                        height: 100% !important
            video
                height: 100%
                width: 100%
                border-radius: 0
                object-fit: contain
                @media (orientation: landscape)
                    object-fit: cover
            .actions
                border-radius: 0 !important

    &::v-deep
        video::-webkit-media-controls, video::-webkit-media-controls-enclosure
            display: none
            -webkit-appearance: none
    &::v-deep
        .player
            width: 300%
            height: 100%
            margin-left: -100%
            position: relative
            padding-top: 0
</style>
